import React, { useState, useEffect } from 'react';
import {
  BookOpen, Plus, Eye, Star, BarChart2, Edit2, Trash2, Save,
  FileText, Video, Mic, Paperclip, CheckCircle, FilePen, List
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminTable from '../../components/admin/AdminTable';
import AdminCard from '../../components/admin/AdminCard';
import AdminModal from '../../components/admin/AdminModal';
import api from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import '../../styles/pages/admin/ContentPage.css';
import '../../styles/admin-shared.css';

const ContentPage = () => {
  // Re-fetch whenever the active program changes — axios already sends
  // X-Program on the request, so the backend returns scoped data.
  const { slug } = useProgram();
  const [contents, setContents] = useState([]);
  const [stats, setStats] = useState({
    totalContent: 0,
    publishedContent: 0,
    totalViews: 0,
    avgRating: 0
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'RECOVERY',
    content_type: 'ARTICLE',
    url: '',
    thumbnail: '',
    author: '',
    duration_minutes: 0,
    published: true
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contentsData, statsData] = await Promise.all([
        api.get('/admin/content').catch(() => ({ data: { data: { contents: [] } } })),
        api.get('/admin/content/stats').catch(() => ({ data: { data: {} } }))
      ]);

      setContents(contentsData.data.data?.contents || []);
      setStats(statsData.data.data || {
        totalContent: 0,
        publishedContent: 0,
        totalViews: 0,
        avgRating: 0
      });
    } catch (error) {
      console.error('Erro ao carregar conteúdos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (content = null) => {
    if (content) {
      setEditing(content);
      setFormData({
        title: content.title,
        description: content.description || '',
        category: content.category || 'RECOVERY',
        content_type: content.content_type || 'ARTICLE',
        url: content.url || '',
        thumbnail: content.thumbnail || '',
        author: content.author || '',
        duration_minutes: content.duration_minutes || 0,
        published: content.published ?? true
      });
    } else {
      setEditing(null);
      setFormData({
        title: '',
        description: '',
        category: 'RECOVERY',
        content_type: 'ARTICLE',
        url: '',
        thumbnail: '',
        author: '',
        duration_minutes: 0,
        published: true
      });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const contentData = {
        ...formData,
        duration_minutes: parseInt(formData.duration_minutes) || null
      };

      if (editing) {
        await api.put(`/admin/content/${editing.id}`, contentData);
      } else {
        await api.post('/admin/content', contentData);
      }
      
      setShowModal(false);
      setEditing(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao salvar conteúdo:', error);
      alert(error.response?.data?.message || 'Erro ao salvar conteúdo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este conteúdo?')) return;
    
    try {
      await api.delete(`/admin/content/${id}`);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir conteúdo:', error);
      alert(error.response?.data?.message || 'Erro ao excluir conteúdo');
    }
  };

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await api.patch(`/admin/content/${id}/publish`, {
        published: !currentStatus
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      alert(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const filteredContents = contents.filter(content => {
    if (activeTab === 'all') return true;
    return content.content_type === activeTab.toUpperCase();
  });

  const columns = [
    {
      key: 'title',
      label: 'Conteúdo',
      render: (value, row) => (
        <div className="content-cell">
          {row.thumbnail && (
            <img src={row.thumbnail} alt={value} className="content-thumbnail" />
          )}
          <div>
            <div className="content-title">{value}</div>
            <div className="content-meta">
              {row.content_type} • {row.category} {row.duration_minutes > 0 && `• ${row.duration_minutes} min`}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'author',
      label: 'Autor',
      render: (value) => value || 'Não informado'
    },
    {
      key: 'view_count',
      label: 'Visualizações',
      render: (value) => <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Eye size={14} /> {value || 0}</span>
    },
    {
      key: 'published',
      label: 'Status',
      render: (value) => (
        <span className={`badge badge-${value ? 'success' : 'warning'}`}>
          {value ? <><CheckCircle size={11} /> Publicado</> : <><FilePen size={11} /> Rascunho</>}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Criado em',
      render: (value) => format(new Date(value), 'dd/MM/yyyy', { locale: ptBR })
    }
  ];

  return (
    <AdminLayout>
      <div className="content-page">
        <div className="page-header">
          <div>
            <h1><BookOpen size={26} /> Conteúdo Educativo</h1>
            <p>Gerencie artigos, vídeos, podcasts e recursos educativos</p>
          </div>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <Plus size={16} /> Adicionar Conteúdo
          </button>
        </div>

        <div className="analytics-cards-grid">
          <AdminCard
            title="Total de Conteúdos"
            value={stats.totalContent || 0}
            subtitle={`${stats.publishedContent || 0} publicados`}
            icon={<BookOpen size={22} />}
            color="blue"
          />
          <AdminCard
            title="Total de Visualizações"
            value={stats.totalViews || 0}
            subtitle="Todas as publicações"
            icon={<Eye size={22} />}
            color="purple"
          />
          <AdminCard
            title="Avaliação Média"
            value={`${(stats.avgRating || 0).toFixed(1)}`}
            subtitle="Baseado em avaliações"
            icon={<Star size={22} />}
            color="yellow"
          />
          <AdminCard
            title="Engajamento"
            value={`${stats.engagementRate || 0}%`}
            subtitle="Taxa de interação"
            icon={<BarChart2 size={22} />}
            color="green"
          />
        </div>

        <div className="tabs-container">
          <button
            className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <List size={15} /> Todos ({contents.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'article' ? 'active' : ''}`}
            onClick={() => setActiveTab('article')}
          >
            <FileText size={15} /> Artigos ({contents.filter(c => c.content_type === 'ARTICLE').length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'video' ? 'active' : ''}`}
            onClick={() => setActiveTab('video')}
          >
            <Video size={15} /> Vídeos ({contents.filter(c => c.content_type === 'VIDEO').length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'podcast' ? 'active' : ''}`}
            onClick={() => setActiveTab('podcast')}
          >
            <Mic size={15} /> Podcasts ({contents.filter(c => c.content_type === 'PODCAST').length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'resource' ? 'active' : ''}`}
            onClick={() => setActiveTab('resource')}
          >
            <Paperclip size={15} /> Recursos ({contents.filter(c => c.content_type === 'RESOURCE').length})
          </button>
        </div>

        <div className="table-section">
          <AdminTable
            columns={columns}
            data={filteredContents}
            loading={loading}
            actions={(row) => (
              <div className="table-actions">
                <button
                  className="table-action-btn edit"
                  onClick={() => handleOpenModal(row)}
                  title="Editar conteúdo"
                >
                  <Edit2 size={14} /> Editar
                </button>
                <button
                  className={`table-action-btn ${row.published ? 'warning' : 'success'}`}
                  onClick={() => handleTogglePublish(row.id, row.published)}
                  title={row.published ? 'Despublicar' : 'Publicar'}
                >
                  {row.published ? <><FilePen size={14} /> Despublicar</> : <><CheckCircle size={14} /> Publicar</>}
                </button>
                <button
                  className="table-action-btn delete"
                  onClick={() => handleDelete(row.id)}
                  title="Excluir conteúdo"
                >
                  <Trash2 size={14} /> Excluir
                </button>
              </div>
            )}
          />
        </div>

        <AdminModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          title={editing ? <><Edit2 size={20} /> Editar Conteúdo</> : <><Plus size={20} /> Adicionar Novo Conteúdo</>}
        >
          <form onSubmit={handleSave} className="content-form">
            <div className="form-group">
              <label htmlFor="title">Título *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Como Superar o Vício, Os 5 Estágios da Recuperação"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Descrição *</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva o conteúdo..."
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="content_type">Tipo *</label>
                <select
                  id="content_type"
                  value={formData.content_type}
                  onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                  required
                >
                  <option value="ARTICLE">Artigo</option>
                  <option value="VIDEO">Vídeo</option>
                  <option value="PODCAST">Podcast</option>
                  <option value="RESOURCE">Recurso</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="category">Categoria *</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="RECOVERY">Recuperação</option>
                  <option value="EDUCATION">Educação</option>
                  <option value="MOTIVATION">Motivação</option>
                  <option value="HEALTH">Saúde</option>
                  <option value="RELATIONSHIPS">Relacionamentos</option>
                  <option value="PSYCHOLOGY">Psicologia</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="url">URL do Conteúdo *</label>
              <input
                type="url"
                id="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://exemplo.com/conteudo"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="thumbnail">URL da Thumbnail</label>
              <input
                type="url"
                id="thumbnail"
                value={formData.thumbnail}
                onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                placeholder="https://exemplo.com/imagem.jpg"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="author">Autor</label>
                <input
                  type="text"
                  id="author"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="Nome do autor"
                />
              </div>

              <div className="form-group">
                <label htmlFor="duration_minutes">Duração (minutos)</label>
                <input
                  type="number"
                  id="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                />
                <span>Publicar imediatamente (visível no app)</span>
              </label>
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-outline"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                {editing ? <><Save size={16} /> Salvar Alterações</> : <><Plus size={16} /> Adicionar Conteúdo</>}
              </button>
            </div>
          </form>
        </AdminModal>
      </div>
    </AdminLayout>
  );
};

export default ContentPage;
